import { create } from "zustand";
import i18n from "../i18n";
import type { SupportedLanguage } from "../i18n";
import { languageLocaleMap } from "../i18n";

interface LanguageStore {
  language: SupportedLanguage;
  isLoading: boolean;
  setLanguage: (language: SupportedLanguage) => Promise<void>;
  loadLanguage: () => Promise<void>;
}

const getSupportedLanguage = (lang: string): SupportedLanguage => {
  if (lang.startsWith("zh")) {
    const region = lang.split("-")[1]?.toUpperCase();
    if (region === "TW" || region === "HK" || region === "MO") {
      return "zh-TW";
    }
    return "zh-CN";
  }
  const primary = lang.split("-")[0];
  if (primary && primary in languageLocaleMap) {
    return primary as SupportedLanguage;
  }
  return "en";
};

const getCurrentLanguage = (): SupportedLanguage => {
  const storedLang = localStorage.getItem("i18nextLng");
  if (storedLang) {
    return getSupportedLanguage(storedLang);
  }
  return getSupportedLanguage(i18n.language || "en");
};

/**
 * Web version of language store - uses localStorage only (no Tauri dependencies)
 */
export const useLanguageStore = create<LanguageStore>((set, get) => ({
  language: getCurrentLanguage(),
  isLoading: true,

  setLanguage: async (language) => {
    try {
      await i18n.changeLanguage(language);
      set({ language });
      // Web version: persist to localStorage
      localStorage.setItem("app-language", language);
    } catch (e) {
      console.error("Failed to change language:", e);
    }
  },

  loadLanguage: async () => {
    set({ isLoading: true });
    try {
      let language: SupportedLanguage | null = null;

      // Check i18next storage first
      const i18nextLang = localStorage.getItem("i18nextLng");
      if (i18nextLang) {
        language = getSupportedLanguage(i18nextLang);
      }

      // Check app-specific storage
      if (!language) {
        const appLang = localStorage.getItem("app-language");
        if (appLang) {
          language = getSupportedLanguage(appLang);
        }
      }

      if (language) {
        await i18n.changeLanguage(language);
        set({ language });
      } else {
        // Use browser's language detection
        const detectedLanguage = getSupportedLanguage(navigator.language || "en");
        await get().setLanguage(detectedLanguage);
      }
    } catch (error) {
      console.error("Failed to load language:", error);
      set({ language: "en" }); // Fallback to English
    } finally {
      set({ isLoading: false });
    }
  },
}));
